import { CopyButton } from './CopyButton';
import type { ForceBankSettlementResponse } from '../lib/types';

function Row({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-right font-medium text-white break-all">{value ?? <span className="text-slate-600">—</span>}</dd>
    </div>
  );
}

function Raw({ value }: { value: unknown }): JSX.Element | null {
  if (value === null || value === undefined) return null;
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-slate-400">Raw response</summary>
      <pre className="mt-1 max-h-64 overflow-auto rounded bg-black/50 p-2 text-xs text-slate-300">
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-lg border border-slate-700 bg-kore-card p-4">
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-300">{title}</h3>
      <dl>{children}</dl>
    </section>
  );
}

export function ResultDashboard({ result }: { result: ForceBankSettlementResponse }): JSX.Element {
  const success = result.overallStatus === 'SUCCESS';
  const bankSearchable = result.bankAcknowledgement.bankSearchableReference ?? result.settlement.bankSearchableReference;

  return (
    <div className="space-y-4">
      {success ? (
        <div className="rounded-lg border border-green-500 bg-green-900/30 p-4" role="status">
          <p className="text-base font-semibold text-green-300">Bank settlement action completed successfully.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Row label="Bank Searchable Reference" value={bankSearchable} />
            <Row label="Settlement Reference" value={result.settlement.settlementReference} />
            <Row label="Payment Confirmation Reference" value={result.reconciliation.confirmationReference} />
            <Row label="PACS.028 Investigation Reference" value={result.pacs028.investigationReference} />
            <Row label="Correlation ID" value={result.correlationId} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton label="Bank Searchable Ref" value={bankSearchable} />
            <CopyButton label="Settlement Ref" value={result.settlement.settlementReference} />
            <CopyButton label="Payment Confirmation Ref" value={result.reconciliation.confirmationReference} />
            <CopyButton label="PACS.028 Ref" value={result.pacs028.investigationReference} />
            <CopyButton label="Correlation ID" value={result.correlationId} />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-500 bg-amber-900/30 p-4" role="alert">
          <p className="text-base font-semibold text-amber-300">Bank settlement action did not fully complete.</p>
          <p className="mt-1 text-sm text-amber-200">Overall status: <span className="font-mono">{result.overallStatus}</span></p>
          {result.errors && result.errors.length > 0 && (
            <ul className="mt-3 space-y-2">
              {result.errors.map((e, i) => (
                <li key={i} className="rounded border border-amber-700 bg-black/30 p-2 text-sm">
                  <div className="font-medium text-amber-200">{e.step} — {e.code}</div>
                  <div className="text-amber-100">{e.message}</div>
                  <Raw value={e.rawResponse} />
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Row label="Correlation ID" value={result.correlationId} />
            <Row label="Idempotency Key" value={result.idempotencyKey} />
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="1. Settlement Result">
          <Row label="KoreID" value={result.koreId} />
          <Row label="Resolved Linked Account" value={result.account.accountIdentifierMasked} />
          <Row label="Settlement Status" value={result.settlement.status} />
          <Row label="Settlement Timestamp UTC" value={result.settlement.timestampUtc} />
          <Row label="Amount" value={result.settlement.amount} />
          <Row label="Currency" value={result.settlement.currency} />
          <Row label="Settlement Reference" value={result.settlement.settlementReference} />
          <Row label="Bank Searchable Reference" value={result.settlement.bankSearchableReference} />
          <Row label="Settlement Correlation ID" value={result.settlement.correlationId} />
          <Raw value={result.settlement.rawResponse} />
        </Section>

        <Section title="2. Resync Result">
          <Row label="Resync Status" value={result.resync.status} />
          <Row label="Resync Timestamp UTC" value={result.resync.timestampUtc} />
          <Row label="Resync Target Account" value={result.resync.targetAccountMasked} />
          <Row label="Resync Correlation ID" value={result.resync.correlationId} />
          <Raw value={result.resync.rawResponse} />
        </Section>

        <Section title="3. PACS.028 Nudge Result">
          <Row label="PACS.028 Status" value={result.pacs028.status} />
          <Row label="Supported" value={String(result.pacs028.supported)} />
          <Row label="PACS.028 Message ID" value={result.pacs028.messageId} />
          <Row label="Investigation Reference" value={result.pacs028.investigationReference} />
          <Row label="PACS.028 Timestamp UTC" value={result.pacs028.timestampUtc} />
          <Row label="Bank/Network Response" value={result.pacs028.bankOrNetworkResponse} />
          <Raw value={result.pacs028.rawResponse} />
        </Section>

        <Section title="4. Bank Acknowledgement">
          <Row label="Bank Ack Status" value={result.bankAcknowledgement.status} />
          <Row label="Bank Ack Timestamp UTC" value={result.bankAcknowledgement.timestampUtc} />
          <Row label="Bank Reference" value={result.bankAcknowledgement.bankReference} />
          <Row label="Bank Searchable Reference" value={result.bankAcknowledgement.bankSearchableReference} />
          <Row label="Bank Response Code" value={result.bankAcknowledgement.responseCode} />
          <Row label="Bank Response Message" value={result.bankAcknowledgement.responseMessage} />
          <Raw value={result.bankAcknowledgement.rawBankIssuedResponse} />
        </Section>

        <Section title="5. Payment Confirmation Reconciliation">
          <Row label="Reconciliation Status" value={result.reconciliation.status} />
          <Row label="Payment Confirmation Status" value={result.reconciliation.paymentConfirmationStatus} />
          <Row label="Confirmation Reference" value={result.reconciliation.confirmationReference} />
          <Row label="Confirmation Timestamp UTC" value={result.reconciliation.confirmationTimestampUtc} />
          <Row label="Match Result" value={result.reconciliation.matchResult} />
          <Row label="Matched Amount" value={result.reconciliation.matchedAmount} />
          <Row label="Matched Currency" value={result.reconciliation.matchedCurrency} />
          <Row label="Exception Reason" value={result.reconciliation.exceptionReason} />
          <Row label="PCR Pending Row Inserted" value={result.reconciliation.pendingRowInserted ? 'yes' : 'no'} />
          <Raw value={result.reconciliation.rawResponse} />
        </Section>

        <Section title="6. Audit and Trace">
          <Row label="Requested By" value={result.requestedBy} />
          <Row label="Bank Request Reference" value={result.bankRequestReference} />
          <Row label="Idempotency Key" value={result.idempotencyKey} />
          <Row label="Correlation ID" value={result.correlationId} />
          <Row label="Causation ID" value={result.causationId} />
          <Row label="Audit Record ID" value={result.audit.auditRecordId} />
          <Row label="Immutable Event ID" value={result.audit.immutableEventId} />
          <Row label="Request Timestamp UTC" value={result.audit.requestTimestampUtc} />
          <Row label="Completion Timestamp UTC" value={result.audit.completionTimestampUtc} />
          <Row label="Sovereign Override Applied" value={result.audit.sovereignOverrideApplied ? 'yes' : 'no'} />
          <Row label="Sovereign Authority Tier" value={result.audit.sovereignAuthorityTier} />
        </Section>
      </div>
    </div>
  );
}
